// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the AYNI chatbot's research and general answers in the
 * contract's envelope (`AgentToolOutcome`, AYNI 3D's
 * `docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md` §3–§4), built from XR's
 * own modules' results. Pure: no DOM, no state; `xrChatExecutor.ts` gathers
 * the inputs from the page, and these say them.
 *
 * How `values` are written (contract §3): the module's own field names
 * (`PinTrend`, `OlsTrend`, `MannKendall`, `PinEmergence`, `PinCorrelation`,
 * `CompareStats`, `ThresholdResult`, `MethodsResult`…);
 * times in milliseconds as ISO strings (`startMs` → `start`); `NaN` as
 * null; the units beside, as `units`. Methods and cautions are English
 * for the model, which answers in the visitor's language, with the
 * methods cited by author and year as the panel and the CSVs cite them.
 */
import type { AgentCaution, AgentLockReason, AgentToolOutcome } from '../ayni3d/shared/protocol'
import type { PinCorrelation, PinEmergence, PinTrend } from '../services/research/pinStats'
import type { CompareStats } from '../services/research/compare'
import type { ThresholdResult, ThresholdRule } from '../services/research/threshold'
import { METHOD_REFERENCES, type MethodRef, type MethodsResult } from '../services/research/methods'

// ------------------------------------------------------------------ the envelope

export function ok(values: Record<string, unknown>, method: string, cautions: AgentCaution[] = [], extra: { references?: readonly string[]; teacher?: Record<string, unknown> } = {}): AgentToolOutcome {
  return {
    status: 'ok',
    values: plain(values) as Record<string, unknown>,
    method,
    cautions,
    ...(extra.references?.length ? { references: [...extra.references] } : {}),
    ...(extra.teacher ? { teacher: plain(extra.teacher) as Record<string, unknown> } : {}),
  }
}

export function unavailable(reason: string, message: string): AgentToolOutcome {
  return { status: 'unavailable', reason, message }
}

export function pending(done: number, total: number, unit: string, message: string): AgentToolOutcome {
  return { status: 'pending', progress: { done, total, unit }, message }
}

export function locked(lock: AgentLockReason, message: string): AgentToolOutcome {
  return { status: 'locked', lock, message }
}

/** Lock messages (owner, 2026-10-07): an adult whose plan lacks a tool hears one neutral sentence. */
export const LOCK_TEXT = {
  plan: "That's part of the Research plans.",
  meeting: 'In this meeting the presenter decides what is on the globe.',
} as const

/** NaN and ±∞ become null, deep; everything else is copied as is. */
export function plain(value: unknown): unknown {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (Array.isArray(value)) return value.map(plain)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) if (v !== undefined) out[k] = plain(v)
    return out
  }
  return value
}

/** Epoch ms as an ISO string; null for anything that is not a time. */
export function iso(ms: number | null | undefined): string | null {
  return typeof ms === 'number' && Number.isFinite(ms) ? new Date(ms).toISOString() : null
}

/** A duration as ISO 8601 (`PT6H`, `P15D`), for gaps between frames. */
export function isoDuration(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null
  const h = Math.round(Math.abs(ms) / 3_600_000)
  if (h === 0) return 'PT0H'
  return h % 24 === 0 ? `P${h / 24}D` : `PT${h}H`
}

/** The methods by author and year, as the panel and the CSVs name them. */
const SHORT_NAME: Readonly<Record<MethodRef, string>> = {
  santer2000: 'Santer et al. (2000)',
  nychka2000: 'Nychka et al. (2000)',
  mann1945: 'Mann (1945)',
  kendall1975: 'Kendall (1975)',
  hamedRao1998: 'Hamed & Rao (1998)',
  sen1968: 'Sen (1968)',
  gilbert1987: 'Gilbert (1987)',
  bretherton1999: 'Bretherton et al. (1999)',
  hawkinsSutton2012: 'Hawkins & Sutton (2012)',
  hovmoller1949: 'Hovmöller (1949)',
  taylor2001: 'Taylor (2001)',
  harrowerBrewer2003: 'Harrower & Brewer (2003)',
  savric2019: 'Šavrič et al. (2019)',
  wmo2017: 'WMO (2017), WMO-No. 1203',
  welford1962: 'Welford (1962)',
  west1979: 'West (1979)',
  moritz2000: 'Moritz (2000)',
}

/**
 * References in short: author, year and DOI (the engine cuts a result at
 * 3,000 characters, and seven APA entries alone are longer). The full list
 * is the methods paragraph's (`methods.get`).
 */
const refs = (...keys: MethodRef[]): string[] =>
  keys.map((k) => {
    const doi = /https:\/\/doi\.org\/\S+/.exec(METHOD_REFERENCES[k])?.[0]
    return doi ? `${SHORT_NAME[k]}, ${doi}` : SHORT_NAME[k]
  })

const caution = (code: string, text: string): AgentCaution => ({ code, text })

// ------------------------------------------------------------------ the pins' statistics (S1, S6, S3)

/** A pin as the chatbot names it: its id (`pin-N`, for the page session) and where it is. */
export interface ChatPin {
  id: string
  lat: number
  lon: number
  label: string
}

const pinValues = (pin: ChatPin) => ({ id: pin.id, label: pin.label, lat: Math.round(pin.lat * 100) / 100, lon: Math.round(pin.lon * 100) / 100 })

export const TREND_METHOD =
  'Least-squares slope with its 95 % interval widened for lag-1 autocorrelation of the residuals (Santer et al. 2000; effective sample size after Nychka et al. 2000), and the Mann–Kendall test with the Hamed & Rao (1998) correction, Kendall\'s τ-b, and Sen\'s (1968) slope with Gilbert\'s (1987) interval. A trend is called rising or falling only when both tests find it at the 5 % level and agree on its sign; Mann–Kendall is never used alone.'

/** Why a trend cannot be given: the module returns null below ten values or past 4,000. */
export type TrendRefusal = 'no-pin' | 'no-series' | 'undated' | 'short' | 'tooMany'

export const TREND_REFUSAL_TEXT: Record<TrendRefusal, string> = {
  'no-pin': 'There is no pin with that id. Read the pins first (pins_read) and use their id.',
  'no-series': 'That pin has no values over time yet: play the dataset so the pin collects them.',
  undated: 'This dataset has no dates, so a trend per year cannot be given.',
  short: 'Fewer than 10 values have been read at that pin; a trend needs at least 10. Let the dataset play further.',
  tooMany: 'More than 4,000 values at that pin; the trend tests are not run on so many.',
}

export function trendOutcome(pin: ChatPin, trend: PinTrend, units: string | null): AgentToolOutcome {
  const { ols, mk } = trend
  const cautions: AgentCaution[] = [
    caution('autocorrelation', 'Values close in time are not independent; the interval and p-value allow for it, and the number of independent values is nEff.'),
  ]
  if (trend.cautions.partial) cautions.push(caution('partial', 'Less than a quarter of the window has been read: the trend describes what has played. Let it play through the whole period.'))
  if (trend.cautions.lowIndependent && !ols.tooFewIndependent) cautions.push(caution('lowIndependent', 'Fewer than ten independent values: read the result with caution.'))
  if (ols.tooFewIndependent) cautions.push(caution('lowIndependent', 'Three or fewer independent values: the slope stands, but its interval and test do not.'))
  if (trend.cautions.manyTies) cautions.push(caution('manyTies', 'More than 30 % of the pairwise slopes are exactly zero (the data\'s steps), which pulls Sen\'s slope towards zero; read the least-squares slope.'))
  if (trend.cautions.cycle) cautions.push(caution('cycle', 'The values are less than a year apart, so a seasonal or daily cycle is likely in them; the tests assume none, so treat the numbers as descriptive.'))
  return ok(
    {
      pin: pinValues(pin),
      n: trend.n,
      unit: trend.unit,
      start: iso(trend.line.startMs),
      end: iso(trend.line.endMs),
      ols: {
        slope: ols.slope,
        ciLow: ols.tooFewIndependent ? null : ols.ciLow,
        ciHigh: ols.tooFewIndependent ? null : ols.ciHigh,
        r2: ols.r2,
        pValue: ols.tooFewIndependent ? null : ols.pValue,
        nEff: ols.nEff,
        lag1: ols.lag1,
        df: ols.df,
        seSlope: ols.seSlope,
        tooFewIndependent: ols.tooFewIndependent,
      },
      mk: { tau: mk.tau, s: mk.s, z: mk.z, pValue: mk.pValue, sen: mk.sen, senLow: mk.senLow, senHigh: mk.senHigh, correction: mk.correction, zeroSlopeShare: mk.zeroSlopeShare },
      verdict: trend.verdict,
      units: units ? `${units} per ${trend.unit}` : `per ${trend.unit}`,
      dataUnits: units,
    },
    TREND_METHOD,
    cautions,
    { references: refs('santer2000', 'nychka2000', 'mann1945', 'kendall1975', 'hamedRao1998', 'sen1968', 'gilbert1987') },
  )
}

export const EMERGENCE_METHOD =
  'Time of emergence (Hawkins & Sutton 2012): the first year after which the 10-year centred running mean stays beyond 2σ of the baseline period\'s variability, with one sign, until the last year the mean can be computed (half a window before the end). σ is the standard deviation of the baseline values about their own least-squares line (or the noise given), never below one step of the data.'

export type EmergenceRefusal = 'no-pin' | 'no-series' | 'undated' | 'short' | 'subAnnual' | 'baseline'

export const EMERGENCE_REFUSAL_TEXT: Record<EmergenceRefusal, string> = {
  'no-pin': TREND_REFUSAL_TEXT['no-pin'],
  'no-series': TREND_REFUSAL_TEXT['no-series'],
  undated: 'This dataset has no dates, so a year of emergence cannot be given.',
  short: 'Emergence needs at least 30 years of values at the pin.',
  subAnnual: 'Emergence is computed on annual (or coarser) values; these are less than a year apart.',
  baseline: 'Fewer than 20 values fall in the baseline period; choose a longer or other period.',
}

export function emergenceOutcome(
  pin: ChatPin,
  r: PinEmergence,
  opts: { units: string | null; ensemble: boolean; noiseGiven: boolean; snap?: (ms: number) => number },
): AgentToolOutcome {
  const e = r.emergence
  const snap = opts.snap ?? ((ms: number) => ms)
  const emergedAt = e.emergedAt === null ? null : snap(e.emergedAt)
  const checkedTo = e.checkedTo === null ? null : snap(e.checkedTo)
  let latest: number | null = null
  for (let i = e.signalToNoise.length - 1; i >= 0; i--) {
    if (Number.isFinite(e.signalToNoise[i])) {
      latest = e.signalToNoise[i]
      break
    }
  }
  const cautions: AgentCaution[] = [
    caution('baselineDependent', 'The year depends on the baseline period chosen: another period can give another year.'),
  ]
  if (opts.ensemble && !opts.noiseGiven) cautions.push(caution('ensemble', 'This dataset is a multi-model mean or median, which varies far less from year to year than any real climate; measured against its own variability, emergence comes far too early. Give a noise level from observations or one model run.'))
  return ok(
    {
      pin: pinValues(pin),
      emerged: emergedAt !== null,
      emergedAt: iso(emergedAt),
      year: emergedAt === null ? null : new Date(emergedAt).getUTCFullYear(),
      direction: e.direction,
      baseMean: e.baseMean,
      baseSd: e.baseSd,
      baseN: e.baseN,
      noiseFrom: e.noiseFrom,
      threshold: e.threshold,
      checkedTo: iso(checkedTo),
      checkedToYear: checkedTo === null ? null : new Date(checkedTo).getUTCFullYear(),
      signalToNoise: latest,
      period: { start: iso(r.periodStartMs), end: iso(r.periodEndMs) },
      windowYears: r.windowYears,
      units: opts.units,
    },
    EMERGENCE_METHOD,
    cautions,
    { references: refs('hawkinsSutton2012') },
  )
}

export const CORRELATION_METHOD =
  'Pearson and Spearman correlation of the two pins\' values on the time steps both have, with significance from the effective number of independent values of two persistent series (Bretherton et al. 1999); the same after removing each series\' least-squares line (detrended); and the lag (in time steps, up to min(10, n/5)) with the largest detrended |r|, its p-value corrected for the lags tried (Bonferroni).'

export type CorrelationRefusal = 'no-pin' | 'no-series' | 'short' | 'window'

export const CORRELATION_REFUSAL_TEXT: Record<CorrelationRefusal, string> = {
  'no-pin': TREND_REFUSAL_TEXT['no-pin'],
  'no-series': 'One of the pins has no values over time yet: play the dataset so the pins collect them.',
  short: 'The two pins share fewer than 10 values; play the dataset further.',
  window: 'The two pins\' values cover different time windows, so they cannot be compared step by step.',
}

export function correlationOutcome(a: ChatPin, b: ChatPin, c: PinCorrelation, stepYears: number | null): AgentToolOutcome {
  const corr = (x: PinCorrelation['pearson']) => ({ r: x.r, n: x.n, nEff: x.nEff, df: x.df, pValue: x.pValue, tooFewIndependent: x.tooFewIndependent })
  const cautions: AgentCaution[] = [caution('notCause', 'A correlation does not show that one causes the other.')]
  if (c.pearson.nEff < 0.9 * c.n) cautions.push(caution('autocorrelation', `The values are persistent: they rest on about ${Math.max(1, Math.round(c.pearson.nEff))} independent values, not ${c.n}.`))
  if (c.cycle) cautions.push(caution('cycle', 'Values less than a year apart share seasonal or daily cycles, which make r larger.'))
  return ok(
    {
      pinA: pinValues(a),
      pinB: pinValues(b),
      n: c.n,
      pearson: corr(c.pearson),
      spearman: corr(c.spearman),
      detrended: c.detrended ? corr(c.detrended) : null,
      bestLag: c.bestLag ? { lag: c.bestLag.lag, r: c.bestLag.r, pCorrected: c.bestLag.pCorrected, tried: c.bestLag.tried } : null,
      lagStepYears: stepYears,
      cycle: c.cycle,
    },
    CORRELATION_METHOD,
    cautions,
    { references: refs('bretherton1999') },
  )
}

// ------------------------------------------------------------------ baseline and change (slice 2, S2)

/** What the Analyze panel holds as a baseline, as `analyzeUI.chatBaselineState` reports it. */
export type ChatBaselineState =
  | { held: false }
  | {
      held: true
      kind: 'frame'
      span: string | null
      region: string
      units: string | null
      /** Whether it applies to the numbers on screen now. */
      fit: 'applies' | 'other-region' | 'other-units' | 'other-grid' | 'other-calendar' | null
    }
  | {
      held: true
      kind: 'period'
      span: string
      /** The period's years (null for a window), and whether they are the dataset's default period. */
      from: number | null
      to: number | null
      isDefault: boolean
      grouping: 'monthly' | 'annual' | 'daily' | 'subdaily' | 'window'
      read: number
      total: number
      enough: boolean
      complete: boolean
      sigma: 'ok' | 'short' | 'tooShort' | 'window'
      minYears: number
      skipped: number
      factor: number
      early: boolean
      groups: number
      flooredCells: number | null
      cellsWithSigma: number | null
      units: string | null
      method: string
      stepping: boolean
    }

export function baselineOutcome(state: ChatBaselineState): AgentToolOutcome {
  if (!state.held) return ok({ held: false }, 'No baseline is held.')
  if (state.kind === 'frame') {
    return ok(
      { held: true, kind: 'frame', span: state.span, region: state.region, units: state.units, fit: state.fit },
      'One frame held as the baseline: its statistics over the region it was measured on, and its values cell by cell for the change on the globe.',
      [caution('regionOnly', 'A held frame\'s statistics apply only to the region it was measured on, and only to data on the same grid and in the same units.')],
    )
  }
  if (!state.complete) {
    return pending(state.read, state.total, 'frames', `reading ${state.read} of ${state.total} frames${state.enough ? '; every group has 80 % of its frames, so the user may press "Use the frames read"' : ''}${state.stepping ? '' : '; playing the dataset reads the rest'}`)
  }
  const cautions: AgentCaution[] = [caution('completeness', 'A cell needs data in at least 80 % of the frames of its group (WMO\'s guideline); filled, interpolated or missing frames count as missing.')]
  if (state.flooredCells && state.cellsWithSigma) {
    cautions.push(caution('sigmaFloor', `In ${state.flooredCells} of ${state.cellsWithSigma} cells with σ, σ was raised to one step of the data, so σ change understates how unusual a value is there.`))
  }
  if (state.grouping === 'daily' || state.grouping === 'subdaily') cautions.push(caution('dailySpread', 'σ of daily (or finer) data includes the day-to-day spread within each month, not only the year-to-year variation.'))
  if (state.sigma === 'short') cautions.push(caution('short', `Each group has only ${state.minYears} years (a climate normal has 30).`))
  if (state.sigma === 'tooShort') cautions.push(caution('tooShort', `Each group has only ${state.minYears} years, too few for σ.`))
  return ok(
    {
      held: true,
      kind: 'period',
      span: state.span,
      from: state.from,
      to: state.to,
      grouping: state.grouping,
      read: state.read,
      total: state.total,
      enough: state.enough,
      sigma: state.sigma,
      minYears: state.minYears,
      skipped: state.skipped,
      factor: state.factor,
      early: state.early,
      groups: state.groups,
      flooredCells: state.flooredCells,
      cellsWithSigma: state.cellsWithSigma,
      units: state.units,
    },
    `Climatology of the period: ${state.method}.`,
    cautions,
    { references: refs('wmo2017', 'welford1962', 'west1979') },
  )
}

export const PERIOD_REFUSAL_TEXT: Record<string, string> = {
  'no-time-axis': 'This dataset has no time axis of frames (a stream\'s .dsa), so a period cannot be read; hold the frame on screen instead.',
  empty: 'No frame of the period can be read (none falls in it, or the publisher measured none).',
  tooFew: 'A month of the period has fewer than two frames that can be read.',
  tooMany: 'The period has too many frames per group to hold.',
  'no-values': 'The dataset on screen carries no values to hold (no dataset, a picture, or classes).',
}

export type ChangeRefusal = 'no-baseline' | 'sigma-needs-period' | 'negative' | 'interval'

export const CHANGE_REFUSAL_TEXT: Record<ChangeRefusal, string> = {
  'no-baseline': 'No baseline is held: hold a frame or a period first (baseline_hold).',
  'sigma-needs-period': 'σ change needs a period baseline with σ (at least 10 years per group).',
  negative: 'This field goes below zero, so its zero is arbitrary and a percentage of it means little; show the change in its units, or σ change.',
  interval: 'A temperature in °C or °F has an arbitrary zero, so a percentage of it depends on the scale; show the change in degrees, or σ change.',
}

export function changeOutcome(mode: 'off' | 'difference' | 'percent' | 'sigma', units: string | null): AgentToolOutcome {
  const shown = mode === 'off' ? null : mode === 'percent' ? '%' : mode === 'sigma' ? 'σ' : units
  return ok(
    { mode, units: shown },
    mode === 'off'
      ? 'The globe shows the dataset\'s values again.'
      : mode === 'sigma'
        ? 'The globe shows each place\'s value minus the climatology\'s mean for its month, divided by σ (standardised anomaly), on a fixed range of ±3σ.'
        : `The globe shows each place's value ${mode === 'percent' ? 'as a percentage change from' : 'minus'} the held baseline, on a fixed colour range; places whose baseline is too close to zero for a percentage are grey.`,
    mode === 'off' ? [] : [caution('analyzeOpen', 'The change shows while the Analyze panel is open; it opened on the Baseline tab.')],
  )
}

// ------------------------------------------------------------------ Compare

export interface ChatCompareResult {
  source: 'overlay' | 'globe'
  other: string
  stats: CompareStats
  ratio: boolean
  cellDegrees: number
  resolution: number | null
  pairing: { status: 'paired' | 'apart' | 'unknown' | 'shown'; gapMs: number | null }
  frames: { a: string; b: string } | null
  units: string | null
  warning: 'subject' | 'change' | null
  region: string
  mapped: 'difference' | 'percent' | null
}

export const COMPARE_METHOD =
  'The dataset (A) minus the other (B) on a common grid at the coarser resolution (a cell counts where at least half of it has data), B converted only through a known unit table; area-weighted bias, root-mean-square difference, centred RMS difference (Taylor 2001) and pattern correlation; frames paired by date within half the coarser time step, or as each globe shows them.'

export const COMPARE_REFUSAL_TEXT: Record<string, string> = {
  units: 'The two have units with no known conversion, so their difference would mean nothing.',
  apart: 'The two frames are further apart in time than half the coarser time step; move one to the other\'s date, or compare them as shown.',
  'outside-dates': 'The overlay has no frame at the dataset\'s date.',
  classified: 'One of the two is made of classes, not amounts: there is nothing to subtract.',
  'no-candidates': 'There is nothing to compare with: no real-time overlay that carries values, and no other globe on screen.',
  'no-overlap': 'The two have no cells with data in common in that region.',
  'no-values': 'The dataset on screen carries no values to compare.',
  'not-found': 'There is no overlay or globe with that id.',
  'ratio:negative': 'A percentage of B is refused: B goes below zero, so its zero is arbitrary.',
  'ratio:interval': 'A percentage of B is refused: a temperature in °C or °F has an arbitrary zero.',
  'ratio:temperature': 'A percentage of B is refused for temperatures.',
  'ratio:change': 'A percentage of B is refused: one of them is a change or anomaly.',
  'ratio:classified': 'A percentage of B is refused: one of them is made of classes.',
}

export function compareOutcome(r: ChatCompareResult): AgentToolOutcome {
  const st = r.stats
  const cautions: AgentCaution[] = [
    caution('noSignificance', 'No significance test is made for the difference of two whole maps: neighbouring places are not independent.'),
  ]
  if (r.warning === 'subject') cautions.push(caution('quantitySubject', 'The two titles name different things: being in the same units does not make them the same quantity.'))
  if (r.warning === 'change') cautions.push(caution('quantityChange', 'One is a change or anomaly and the other a value, so their difference mixes two kinds of number.'))
  if (r.pairing.status === 'shown' || r.pairing.status === 'unknown' || (r.pairing.gapMs ?? 0) > 0) {
    cautions.push(caution('pairingGap', r.pairing.status === 'shown' ? 'The frames are compared as each globe shows them, not matched by date.' : r.pairing.status === 'unknown' ? 'The dates of the two frames are not known, so they could not be matched.' : 'The two frames are not at exactly the same time.'))
  }
  if (r.resolution !== null) cautions.push(caution('resolution', `A difference smaller than ${r.resolution} ${r.units ?? ''} is rounding in the data.`.trimEnd()))
  return ok(
    {
      source: r.source,
      other: r.other,
      region: r.region,
      cells: st.cells,
      areaKm2: st.areaKm2,
      overlap: st.overlap,
      bias: st.bias,
      rmsd: st.rmsd,
      centredRmsd: st.centredRmsd,
      patternCorrelation: st.patternCorrelation,
      meanA: st.meanA,
      meanB: st.meanB,
      ...(r.ratio ? { relativeBias: st.relativeBias } : {}),
      cellDegrees: r.cellDegrees,
      resolution: r.resolution,
      pairing: { status: r.pairing.status, gap: isoDuration(r.pairing.gapMs) },
      frames: r.frames,
      onGlobe: r.mapped,
      units: r.units,
    },
    COMPARE_METHOD,
    cautions,
    { references: refs('taylor2001') },
  )
}

// ------------------------------------------------------------------ area over a threshold (S7)

export const THRESHOLD_TEXT_METHOD =
  'Area over a threshold on the frame on screen: each cell of the dataset\'s own grid over the region (no regridding), compared on its value (strictly above or below), with exact cell areas on a sphere of the Earth\'s surface area (WGS84 authalic radius, Moritz 2000); cells without data, or without the baseline where a change is compared, are excluded and counted. The low–high range allows for cells within one data step of the threshold.'

export function thresholdOutcome(r: ThresholdResult, opts: { rule: ThresholdRule; ruleText: string; units: string | null; frame: string | null; region: string; sigmaFloored: boolean }): AgentToolOutcome {
  const cautions: AgentCaution[] = []
  if (r.cellsAmbiguous > 0) cautions.push(caution('band', `${r.cellsAmbiguous} cells are within one data step of the threshold and may be on either side of it: the area is between areaLowKm2 and areaHighKm2.`))
  if (opts.rule.mode === 'sigma') {
    cautions.push(caution('notMarineHeatwave', 'This is not a marine-heatwave definition (Hobday et al. 2016 use the 90th percentile lasting five days or more); it measures one frame and does not test duration.'))
    if (opts.sigmaFloored) cautions.push(caution('sigmaFloor', 'Where the spread is below one step of the data, σ was raised to that step, so σ change understates there.'))
  }
  return ok(
    {
      areaKm2: r.areaKm2,
      areaLowKm2: r.areaLowKm2,
      areaHighKm2: r.areaHighKm2,
      areaWithDataKm2: r.areaWithDataKm2,
      regionAreaKm2: r.regionAreaKm2,
      percent: r.percent,
      cellsOver: r.cellsOver,
      cellsAmbiguous: r.cellsAmbiguous,
      cellsNoData: r.cellsNoData,
      frame: opts.frame,
      region: opts.region,
      rule: { mode: opts.rule.mode, comparator: opts.rule.comparator, threshold: opts.rule.threshold, text: opts.ruleText },
      units: opts.rule.mode === 'sigma' ? 'σ' : opts.units,
    },
    THRESHOLD_TEXT_METHOD,
    cautions,
    { references: refs('moritz2000') },
  )
}

// ------------------------------------------------------------------ Line over time (Hovmöller)

export interface ChatHovmollerSummary {
  line: { from: { lat: number; lon: number }; to: { lat: number; lon: number }; lengthKm: number; samples: number }
  startMs: number
  endMs: number
  dated: boolean
  rows: number
  read: number
  frames: number | null
  mode: 'values' | 'change' | 'sigma'
  min: number | null
  max: number | null
  mean: number | null
  step: number | null
  units: string | null
  spacingKm: number
  /** AYNI (owner, 2026-10-18): when and where along the line the highest and lowest cells were read (the first frame holding each; `others` share it). */
  maxAt?: ChatHovEnd | null
  minAt?: ChatHovEnd | null
}

export interface ChatHovEnd {
  timeMs: number
  distanceKm: number
  lat: number
  lon: number
  others: number
}

export const HOVMOLLER_TEXT_METHOD =
  'Hovmöller diagram (Hovmöller 1949): the values along the Line (a great circle, or a parallel when it follows a latitude), one row per frame read during playback (time running downward), one column per point (one per grid cell crossed); lowest, highest and the unweighted mean over the cells read.'

export function hovmollerOutcome(s: ChatHovmollerSummary): AgentToolOutcome {
  const cautions: AgentCaution[] = []
  if (s.read < s.rows) cautions.push(caution('rowsFilling', `${s.read} of ${s.rows} rows have been read: rows fill as playback passes their frames.`))
  if (s.step !== null) cautions.push(caution('quantisation', `Values are rounded: one step is ${s.step} ${s.units ?? ''}.`.trimEnd()))
  cautions.push(caution('spacing', `Points are about ${Math.round(s.spacingKm)} km apart along the line.`))
  const time = (ms: number) => (s.dated ? iso(ms) : `${Math.round(ms / 1000)} s into the video`)
  return ok(
    {
      line: s.line,
      start: time(s.startMs),
      end: time(s.endMs),
      rows: s.rows,
      read: s.read,
      frames: s.frames,
      mode: s.mode,
      min: s.min,
      max: s.max,
      mean: s.mean,
      ...(s.maxAt ? { highestAt: { time: time(s.maxAt.timeMs), distanceKm: Math.round(s.maxAt.distanceKm), lat: s.maxAt.lat, lon: s.maxAt.lon, otherFramesWithTheSameValue: s.maxAt.others } } : {}),
      ...(s.minAt ? { lowestAt: { time: time(s.minAt.timeMs), distanceKm: Math.round(s.minAt.distanceKm), lat: s.minAt.lat, lon: s.minAt.lon, otherFramesWithTheSameValue: s.minAt.others } } : {}),
      step: s.step,
      units: s.mode === 'sigma' ? 'σ' : s.units,
    },
    HOVMOLLER_TEXT_METHOD,
    cautions,
    { references: refs('hovmoller1949') },
  )
}

// ------------------------------------------------------------------ methods, figure, bundle, citation

export function methodsOutcome(m: MethodsResult): AgentToolOutcome {
  return ok({ paragraph: m.paragraph, cited: [...m.cited] }, 'Built from what was computed in this session, from the app\'s own state; English, as it is pasted into a paper.', [], { references: m.references })
}

export function citeOutcome(format: 'text' | 'bibtex' | 'ris', citation: string, viewLink: string): AgentToolOutcome {
  return ok({ citation, viewLink, format }, 'The dataset\'s own citation with its credit and licences, AYNI XR as software, and a link that reopens this frame, camera and layers.')
}

// ------------------------------------------------------------------ explain a reading

export interface ChatExplain {
  units: string | null
  range: { min: number; max: number }
  step: number | null
  palette: string
  noData: string
  classes: string[]
  scale: 'linear' | 'log' | 'classified'
  frame: string | null
  point?: { lat: number; lon: number; value: number | null; noData: boolean; change?: number | null; changeMode?: string | null; changeUnits?: string | null }
}

export function explainOutcome(e: ChatExplain): AgentToolOutcome {
  const cautions: AgentCaution[] = []
  if (e.step !== null) cautions.push(caution('quantisation', `Values are rounded: one step is ${e.step} ${e.units ?? ''}; smaller differences are not real.`.trimEnd()))
  if (e.scale === 'log') cautions.push(caution('logScale', 'The colours follow a logarithmic scale: equal colour steps are equal ratios, not equal amounts.'))
  return ok(
    {
      units: e.units,
      range: e.range,
      step: e.step,
      palette: e.palette,
      noData: e.noData,
      classes: e.classes,
      scale: e.scale,
      frame: e.frame,
      ...(e.point ? { point: e.point } : {}),
    },
    'Read from the dataset\'s own colour scale (its range, step and palette) and, for a point, the value under it on the frame on screen.',
    cautions,
  )
}
